
import React, { useMemo, useState } from 'react';
import { Order, SystemConfig, Driver } from '../types';
import { generateAnalytics } from '../services/analyticsService';
import { BarChart3, TrendingUp, DollarSign, Clock, Map as MapIcon, ShieldCheck, PieChart, Target, Info, Flame, Navigation, Calendar, LayoutDashboard, PackageSearch, Sparkles, AlertTriangle, Check } from 'lucide-react';
import { AnalyticsMapEngine } from './map/AnalyticsMapEngine';
import { DriverPerformanceTable } from './analytics/DriverPerformanceTable';
import { CompliancePanel } from './analytics/CompliancePanel';
import { subDays, startOfDay, endOfDay, isWithinInterval, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getOrderCompletionTimestamp } from '../services/driverDailyStats';

interface Props {
  historicalOrders: Order[];
  config: SystemConfig;
  drivers: Driver[];
  storeId?: string;
  isAdmin?: boolean;
}

type DateShortcut = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';

export const AnalyticsDashboard: React.FC<Props> = ({ historicalOrders, config, drivers, storeId, isAdmin }) => {
  const [shortcut, setShortcut] = useState<DateShortcut>('30d');

  // v50.1: Date Range Source of Truth
  // Inicializamos com 30 dias atrás até hoje
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });

  const handleShortcutClick = (id: DateShortcut) => {
    const now = new Date();
    let start = now;
    let end = now;

    if (id === 'today') { start = now; end = now; }
    else if (id === 'yesterday') { start = subDays(now, 1); end = subDays(now, 1); }
    else if (id === '7d') { start = subDays(now, 7); }
    else if (id === '30d') { start = subDays(now, 30); }
    else if (id === '90d') { start = subDays(now, 90); }

    setDateRange({
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    });
    setShortcut(id);
  };

  const activeInterval = useMemo(() => {
    return {
      start: startOfDay(new Date(dateRange.start + 'T00:00:00')),
      end: endOfDay(new Date(dateRange.end + 'T23:59:59'))
    };
  }, [dateRange]);

  const isDemo = !historicalOrders || historicalOrders.length === 0;

  const displayOrders = useMemo(() => {
    if (!isDemo) {
      return historicalOrders.filter(order => {
        const date = new Date(getOrderCompletionTimestamp(order) || order.createdAt);
        return isWithinInterval(date, activeInterval);
      });
    }

    return Array.from({ length: 50 }).map((_, i) => ({
      id: `DEMO-${i}`,
      customerName: `Cliente Demo ${i}`,
      streetAddress: `Rua Exemplo, ${i * 10}`,
      neighborhood: i % 3 === 0 ? 'Bairro Central' : i % 2 === 0 ? 'Zona Sul' : 'Zona Norte',
      createdAt: Date.now() - (Math.random() * 86400000 * 30),
      orderValue: 40 + Math.random() * 100,
      deliveryFee: i % 4 === 0 ? 0 : 7,
      status: 'delivered',
      lat: config.businessLat + (Math.random() - 0.5) * 0.05,
      lng: config.businessLng + (Math.random() - 0.5) * 0.05,
      timeLimit: 45,
      deliveredAt: Date.now(),
      departedHubAt: Date.now() - 15 * 60000,
      arrivedAt: Date.now() - 5 * 60000,
      distance: 1 + Math.random() * 8,
      costThirdParty: 12
    } as unknown as Order));
  }, [historicalOrders, isDemo, activeInterval, config.businessLat, config.businessLng]);

  const analytics = useMemo(() => generateAnalytics(displayOrders, config, drivers), [displayOrders, config, drivers]);

  const ShortcutBtn = ({ id, label }: { id: DateShortcut, label: string }) => (
    <button
      type="button"
      onClick={() => handleShortcutClick(id)}
      className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all border ${shortcut === id ? 'bg-sal-cyber-teal text-sal-gunmetal border-sal-cyber-teal shadow-lg' : 'bg-card-bg border-border-dim text-text-muted hover:bg-card-bg/80'}`}
    >
      {label}
    </button>
  );

  const MetricCard = ({ title, value, subValue, icon: Icon, color, benchmark }: any) => (
    <div className="bg-sal-gunmetal-light/40 p-6 rounded-[2.5rem] border border-border-dim shadow-2xl relative overflow-hidden group hover:border-sal-cyber-teal/30 transition-all">
      <div className={`absolute -right-4 -top-4 opacity-[0.05] group-hover:opacity-[0.1] transition-opacity ${color}`}>
        {Icon && <Icon className="w-24 h-24" />}
      </div>
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-2">
          <div className="text-xs font-black text-text-muted uppercase tracking-widest">{title}</div>
        </div>
        <div className="text-3xl font-black text-text-primary tracking-tighter mb-1">{value}</div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold uppercase ${color}`}>{subValue}</span>
          {benchmark && <span className="text-xs text-text-muted font-bold uppercase tracking-tighter border-l border-border-dim pl-2">Referência: {benchmark}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-sal-gunmetal flex flex-col overflow-hidden animate-in fade-in duration-500 font-sans">
      {isDemo && (
        <div className="bg-sal-cyber-teal/10 border-b border-sal-cyber-teal/20 px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-sal-cyber-teal animate-pulse" />
            <span className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.3em]">Ambiente de Demonstração</span>
          </div>
        </div>
      )}

      <div className="p-8 border-b border-border-dim flex flex-col xl:flex-row xl:items-center justify-between gap-6 bg-card-bg/20">
        <div className="flex items-center gap-5">
          <div className="p-4 bg-sal-cyber-teal/10 rounded-3xl border border-sal-cyber-teal/20 text-sal-cyber-teal"><BarChart3 className="w-8 h-8" /></div>
          <div>
            <h2 className="text-3xl font-black text-text-primary uppercase tracking-tighter">Radar de Lucratividade</h2>
            <p className="text-xs text-text-muted font-bold uppercase tracking-[0.3em] flex items-center gap-2">
              <Calendar className="w-4 h-4 text-sal-cyber-teal" />
              {format(activeInterval.start, "dd 'de' MMM", { locale: ptBR })} - {format(activeInterval.end, "dd 'de' MMM 'de' yyyy", { locale: ptBR })}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 bg-sal-gunmetal/50 p-2 rounded-[1.5rem] border border-border-dim">
          <ShortcutBtn id="today" label="Hoje" />
          <ShortcutBtn id="yesterday" label="Ontem" />
          <ShortcutBtn id="7d" label="7 Dias" />
          <ShortcutBtn id="30d" label="30 Dias" />
          <ShortcutBtn id="90d" label="90 Dias" />
          <div className="w-px h-6 bg-border-dim mx-1" />
          <div className="flex items-center gap-2 px-3">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => { setDateRange({ ...dateRange, start: e.target.value }); setShortcut('custom'); }}
              className="bg-card-bg/40 border border-border-dim rounded-lg px-2 py-1.5 text-xs font-black text-text-primary outline-none focus:border-sal-cyber-teal transition-all"
            />
            <span className="text-xs text-text-muted font-bold uppercase">até</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => { setDateRange({ ...dateRange, end: e.target.value }); setShortcut('custom'); }}
              className="bg-card-bg/40 border border-border-dim rounded-lg px-2 py-1.5 text-xs font-black text-text-primary outline-none focus:border-sal-cyber-teal transition-all"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 space-y-8 no-scrollbar pb-32">
        {isDemo && (
          <div className="bg-card-bg/40 p-10 rounded-[3rem] border border-border-dim relative overflow-hidden flex flex-col md:flex-row items-center gap-10">
            <div className="w-40 h-40 bg-sal-cyber-teal/5 rounded-[2.5rem] border border-sal-cyber-teal/10 flex items-center justify-center shrink-0"><LayoutDashboard className="w-16 h-16 text-sal-cyber-teal opacity-50" /></div>
            <div>
              <h3 className="text-3xl font-black text-text-primary uppercase tracking-tighter mb-3">Bem-vindo à sua Central de Gestão</h3>
              <p className="text-base text-text-muted leading-relaxed max-w-2xl font-medium">Esta área revela o invisível. Como você ainda não possui dados históricos, estamos mostrando uma simulação.</p>
              <div className="flex items-center gap-4 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20 text-xs font-black uppercase"><Check className="w-3 h-3" /> Mapa de Sangramento</div>
                <div className="flex items-center gap-2 px-4 py-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20 text-xs font-black uppercase"><Check className="w-3 h-3" /> Inteligência de Preços</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <MetricCard title="Taxa de Sucesso SLA" value={`${analytics.slaSuccessRate.toFixed(1)}%`} subValue="Entregas no Prazo" color="text-sal-cyber-teal" benchmark="92%" icon={ShieldCheck} />
          <MetricCard title="Saúde Financeira Frete" value={`${analytics.profitMarginOnFee.toFixed(1)}%`} subValue="Lucro sobre Taxas" color={analytics.profitMarginOnFee > 0 ? "text-emerald-500 dark:text-emerald-400" : "text-rose-500"} benchmark="+15%" icon={PieChart} />
          <MetricCard title="Tempo de Serviço" value={`${analytics.avgServiceTime.toFixed(1)} min`} subValue="Média na porta" color="text-blue-400" benchmark="4.0 min" icon={Clock} />
          <MetricCard title="Ticket Médio" value={`R$ ${analytics.avgTicket.toFixed(0)}`} subValue="Valor por Pedido" color="text-purple-400" icon={DollarSign} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7"><div className="bg-sal-gunmetal-light/30 rounded-[3rem] border border-border-dim overflow-hidden h-[500px] relative shadow-2xl group"><div className="absolute top-8 left-8 z-10 bg-sal-gunmetal/90 backdrop-blur-xl px-5 py-3 rounded-2xl border border-border-dim flex items-center gap-3"><div className="w-3 h-3 bg-rose-500 rounded-full animate-ping" /><div className="text-xs font-black text-text-primary uppercase tracking-widest">Mapa de Sangramento</div></div><AnalyticsMapEngine orders={displayOrders} drivers={drivers} config={config} /></div></div>
          <div className="lg:col-span-5"><div className="bg-card-bg/40 p-8 rounded-[3rem] border border-border-dim h-full relative overflow-hidden group"><div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity"><Target className="w-48 h-48 text-sal-cyber-teal" /></div><h3 className="text-base font-black text-text-primary uppercase tracking-widest mb-2 flex items-center gap-2">
            <Target className="w-5 h-5 text-sal-cyber-teal" /> Inteligência de Precificação
          </h3>
            <p className="text-xs text-text-muted font-bold uppercase mb-8 leading-relaxed">
              Análise baseada em <span className="text-sal-cyber-teal">distância real percorrida</span> ou estimativa de rota (Linha Reta x 1.3). Reflete o custo logístico efetivo de cada raio.
            </p><div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[380px] overflow-y-auto no-scrollbar pr-1">{Object.values(analytics.pricingIntelligence).map((bucket: any) => (<div key={bucket.radius} className="bg-black/5 dark:bg-white/[0.03] p-3 rounded-xl border border-border-dim/50 flex flex-col gap-2 hover:bg-black/10 dark:hover:bg-white/[0.06] transition-all"><div className="flex justify-between items-center"><div><div className="text-xs font-black text-text-primary uppercase mb-0.5">Raio {bucket.radius}</div><div className="text-xs text-text-muted font-bold uppercase">{bucket.orderCount} Pedidos</div></div><div className="text-right"><div className={`text-xs font-black ${bucket.gap > 0 ? 'text-rose-500' : 'text-emerald-500 dark:text-emerald-400'}`}>{bucket.gap > 0 ? `Subir R$ ${bucket.gap.toFixed(2)}` : 'Equilibrado'}</div><div className="text-xs text-text-muted font-bold uppercase tracking-tighter">Sugerido</div></div></div><div className="pt-2 border-t border-border-dim/50"><p className="text-xs text-text-muted font-mono leading-tight">{bucket.gap > 0 ? <><span className="text-rose-400 font-bold">Prejuízo:</span> Custo (R$ {bucket.avgRealCost.toFixed(2)}) &gt; Taxa (R$ {bucket.avgCustomerFee.toFixed(2)}).</> : <><span className="text-emerald-500 font-bold">Saudável:</span> Taxa (R$ {bucket.avgCustomerFee.toFixed(2)}) cobre Custo (R$ {bucket.avgRealCost.toFixed(2)}).</>}</p></div></div>))}</div></div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <DriverPerformanceTable drivers={drivers} orders={displayOrders} config={config} />
          {storeId && isAdmin && <CompliancePanel storeId={storeId} isAdmin={isAdmin} />}
        </div>
      </div>
    </div>
  );
};
